export default {
  name: 'ComplianceMonitoring',
  props: {
    programs: {
      type: Array,
      default: () => []
    },
    fetchAnswers: {
      type: Function,
      required: true
    },
    fetchChecks: {
      type: Function,
      required: true
    }
  },
  data: () => ({
    openMenuId: null,
    internalPrograms: [],
    openProjectId: null,
    allProjects: [],
    currentProject: null
    // Remove modalAnswer, modalChecks, modalLoadingChecks
  }),
  computed: {
    projectStatuses() {
      const statuses = {};

      this.internalPrograms.forEach(program => {
        program.projects.forEach(project => {
          switch (project.compliance_status) {
            case 'COMPLIANT':
              statuses[project.id] = 'Compliant';
              break;
            case 'NON_COMPLIANT':
              statuses[project.id] = 'Non-Compliant';
              break;
            case 'GRACE_PERIOD':
              statuses[project.id] = 'Risk of Non-Compliance';
              break;
            case 'PENDING':
              statuses[project.id] = 'Pending';
              break;
            default:
              statuses[project.id] = 'Pending'; // fallback if status is undefined or unknown
          }
        });
      });

      return statuses;
    }
  },
  watch: {
    programs: {
      immediate: true,
      deep: true,
      handler(newVal) {
        // Deep clone programs and initialize project properties
        this.internalPrograms = JSON.parse(JSON.stringify(newVal)).map(program => ({
          ...program,
          projects: program.projects.map(project => ({
            ...project,
            loadingAnswers: false,
            answers: [],
            answersPagination: {
              page: 1,
              page_size: 5,
              has_more: false
            },
            loadingChecks: false
          }))
        }));

        // Flatten all projects for easy access
        this.allProjects = this.internalPrograms.flatMap(p => p.projects);
      }
    },
    openMenuId(newVal) {
      if (newVal) {
        document.addEventListener('click', this.handleOutsideClick);
      } else {
        document.removeEventListener('click', this.handleOutsideClick);
      }
    }
  },
  methods: {
    get_status_badge_class(status) {
      switch((status || '').toLowerCase()) {
        case 'compliant': return 'bg-success text-white';
        case 'non-compliant': return 'bg-danger text-white';
        case 'risk of non-compliance': return 'bg-info text-white';
        case 'pending': return 'bg-warning text-dark';
        default: return 'bg-secondary text-white';
      }
    },
    async toggle_project(project) {
      if (this.openProjectId === project.id) {
        this.openProjectId = null;
        this.currentProject = null;
      } else {
        this.openProjectId = project.id;
        this.currentProject = project;

        // Load answers if not already loaded
        if (!project.answers.length && !project.loadingAnswers) {
          await this.load_answers(project);
        }
      }
    },
    async load_answers(project) {
      project.loadingAnswers = true;

      // Use the fetchAnswers prop function
      this.fetchAnswers(project.question_id)
        .then(answers => {
          project.answers = answers.map(answer => ({
            ...answer,
            showHistory: false,
            loadingChecks: false,
            checks: [],
            checksPagination: {
              page: 1,
              page_size: 3,
              has_more: false
            }
          }));
          project.answersPagination.has_more = answers.length >= project.answersPagination.page_size;
        })
        .catch(() => {
          project.answers = [];
        })
        .finally(() => {
          project.loadingAnswers = false;
        });
    },
    toggleAnswerHistory(project, answer) {
      answer.showHistory = !answer.showHistory;
      if (answer.showHistory && !answer.checks.length && !answer.loadingChecks) {
        this.loadChecks(project, answer);
      }
    },
    loadChecks(project, answer) {
      answer.loadingChecks = true;

      // Use the fetchChecks prop function
      this.fetchChecks(project.questionId, answer.id, answer.checksPagination.page, answer.checksPagination.page_size)
        .then(checks => {
          answer.checks = checks;
          answer.checksPagination.has_more = checks.length >= answer.checksPagination.page_size;
        })
        .catch(() => {
          answer.checks = [];
        })
        .finally(() => {
          answer.loadingChecks = false;
        });
    },
    fetchMoreAnswers(project) {
      if (project.loadingAnswers) return;

      project.answersPagination.page += 1;
      this.load_answers(project);
    },
    fetchMoreChecks(project, answer) {
      if (answer.loadingChecks) return;
      answer.checksPagination.page += 1;
      this.loadChecks(project, answer);
    },
    handleOutsideClick(event) {
      if (!event.target.closest('.dropdown')) {
        this.openMenuId = null;
      }
    },
    toggleMenu(projectId, event) {
      event.stopPropagation();
      this.openMenuId = this.openMenuId === projectId ? null : projectId;
      if (this.openMenuId) {
        // Set modalProject for the modal
        const modalProject = this.allProjects.find(p => p.id === projectId);
        if (modalProject && (!modalProject.answers || !modalProject.answers.length)) {
          this.load_answers(modalProject);
        }
      }
    },
    toggleProgram(programId) {
      this.internalPrograms = this.internalPrograms.map(program =>
        program.id === programId ? { ...program, isOpen: !program.isOpen } : program
      );

      const toggled_program = this.internalPrograms.find(p => p.id === programId);
      if (toggled_program && !toggled_program.isOpen) {
        // Close open project when closing program
        this.openProjectId = null;
        this.currentProject = null;
      }
    },
    extract_file_name(answer) {
      if (!answer) return 'No file';
      if (typeof answer === 'string') {
        try {
          const url = new URL(answer.split('?')[0]);
          return decodeURIComponent(url.pathname.split('/').pop());
        } catch {
          return 'Invalid file URL';
        }
      }
      if (answer.filename) return answer.filename;
      if (answer.url) {
        try {
          const url = new URL(answer.url.split('?')[0]);
          return decodeURIComponent(url.pathname.split('/').pop());
        } catch {
          return 'Invalid file URL';
        }
      }
      return 'Unknown file';
    },
    format_date(date_string) {
      if (!date_string) return 'N/A';
      const date = new Date(date_string);
      return date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    format_frequency(freq) {
      const FREQUENCY_MAP = {
        ONCE: 'One-time',
        DAILY: 'Daily',
        WEEKLY: 'Weekly',
        MONTHLY: 'Monthly',
        QUARTERLY: 'Quarterly',
        ANNUALLY: 'Annually'
      };

      return Reflect.get(FREQUENCY_MAP, freq) || freq;
    },
    get_history_status_class(check) {
      return check.status === 'COMPLIANT' 
        ? 'border-success' 
        : 'border-danger';
    },
    get_history_status_label(check) {
      switch (check.status) {
        case 'COMPLIANT': return 'Compliant';
        case 'NON_COMPLIANT': return 'Non-Compliant';
        case 'PENDING': return 'Pending';
        case 'GRACE_PERIOD': return 'Grace Period';
        case 'WAIVED': return 'Waived';
        default: return check.status;
      }
    },
    get_history_badge_class(status) {
      switch (status) {
        case 'COMPLIANT': return 'bg-success text-white';
        case 'NON_COMPLIANT': return 'bg-danger text-white';
        case 'PENDING': return 'bg-warning text-dark';
        case 'GRACE_PERIOD': return 'bg-info text-white';
        case 'WAIVED': return 'bg-secondary text-white';
        default: return 'bg-secondary text-white';
      }
    },
    // Open modal and load all answers for the selected project if not loaded
    openChecksModal(project, answer) {
      this.modalAnswer = answer;
      this.modalChecks = [];
      this.modalLoadingChecks = true;
      // Open the modal (Bootstrap 5)
      const modalId = `#compliance_overview_modal_${project.id}`;
      if (window.bootstrap && window.bootstrap.Modal) {
        const modal = new window.bootstrap.Modal(document.querySelector(modalId));
        modal.show();
      } else {
        // fallback for other modal libraries or manual triggering
        document.querySelector(modalId)?.classList.add('show');
        document.querySelector(modalId)?.setAttribute('style', 'display: block;');
      }

      this.fetchChecks(project.questionId, answer.id, 1, 10)
        .then(checks => {
          this.modalChecks = checks;
        })
        .catch(() => {
          this.modalChecks = [];
        })
        .finally(() => {
          this.modalLoadingChecks = false;
        });
    },
    // In modal: expand/collapse answer and fetch checks if needed
    toggleModalAnswerHistory(answer) {
      answer.showHistory = !answer.showHistory;
      if (answer.showHistory && (!answer.checks || answer.checks.length === 0) && !answer.loadingChecks) {
        answer.loadingChecks = true;
        this.fetchChecks(answer.id, 1)
          .then(checks => {
            answer.checks = checks;
          })
          .catch(() => {
            answer.checks = [];
          })
          .finally(() => {
            answer.loadingChecks = false;
          });
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};